import React from 'react';
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers} from "formik";
import {LessonActivityPlan, LessonActivityPlanData, LessonPlan, LessonPlanData} from "types/LessonPlan";
import Field from "components/Form/Field";
import Button from "components/Button";
import Toggle from "components/Form/Toggle";
import LessonActivityPlanForm from "pages/LessonPlan/Form/LessonActivityPlanForm";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import FieldHeader from "components/Form/FieldHeader";
import {ClipboardCheckIcon, ClipboardListIcon} from "@heroicons/react/outline";
import FormControls from "components/FormControls";

type Props = {
    lessonPlan?: LessonPlan
    onSubmit: (values: LessonPlanData, helpers: FormikHelpers<LessonPlanData>) => Promise<any>
}

function activityData(activity: LessonActivityPlan): LessonActivityPlanData {
    return {
        id: activity.id,
        name: activity.name,
        description: activity.description,
        duration: activity.duration.as('minutes'),
        glossary: activity.glossary,
        resources: activity.resources,
        exhibits: activity.exhibits.map(exhibit => exhibit.id),
        media: activity.media.map(file => ({id: file.id}))
    }
}

const emptyActivity: LessonActivityPlanData = {
    name: '',
    description: '',
    duration: 15,
    glossary: [],
    resources: [],
    exhibits: [],
    media: [],
}

function sanitiseValues(values: LessonPlanData): LessonPlanData {
    // Remove any empty glossary entries, resources and any invalid exhibit IDs to prevent them being sent to the API.
    // This can happen when the "Add..." button is pressed but then the value isn't filled in
    values.activities?.map(activityValues => {
        activityValues.glossary  = activityValues.glossary.filter(entry => entry.word && entry.definition)
        activityValues.resources = activityValues.resources.filter(resource => !!resource.name)
        activityValues.exhibits  = activityValues.exhibits.filter(exhibitId => !!exhibitId)

        return activityValues
    })

    return values
}

function LessonPlanForm(props: Props) {
    const exhibitFilters = {published:true, page: 1, per_page: 200}

    const {data: exhibits} = useQuery(
        ['exhibits', exhibitFilters],
        () => ngage.exhibits.filtered(exhibitFilters)
    )

    const initialValues: LessonPlanData = {
        name: props.lessonPlan?.name || '',
        description: props.lessonPlan?.description || '',
        target_audience: props.lessonPlan?.target_audience || '',
        skills: props.lessonPlan?.skills || [''],
        outcomes: props.lessonPlan?.outcomes || [''],
        activities: props.lessonPlan?.activities.map(activityData) || [{...emptyActivity}],
        is_published: props.lessonPlan?.is_published || false
    }

    return (
        <Formik initialValues={initialValues} onSubmit={(values, helpers) => props.onSubmit(sanitiseValues(values), helpers)}>
            {formik => (
                <Form className="space-y-8 divide-y divide-gray-200">
                    <div className="shadow sm:rounded-md">
                        <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                            <div className="space-y-8 divide-y divide-gray-200">
                                <div>
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Key Information
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Enter basic details about the lesson plan
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-4">

                                            {/* Name */}
                                            <Field type="text"
                                                   label="Name"
                                                   name="name"
                                                   id="name"
                                                   autoComplete="name"
                                                   maxLength="255"
                                                   autoFocus
                                                   required
                                            />
                                        </div>

                                        {/* Target Audience */}
                                        <div className="sm:col-span-2">
                                            <Field type="text"
                                                   label="Target Audience"
                                                   name="target_audience"
                                                   id="target_audience"
                                                   autoComplete="off"
                                                   maxLength="255"
                                                   required
                                            />
                                        </div>

                                        {/* Description */}
                                        <div className="sm:col-span-6">
                                            <Field as="textarea"
                                                   rows={4}
                                                   label="Description"
                                                   name="description"
                                                   id="description"
                                                   autoComplete="off"
                                                   maxLength="50000"
                                                   required
                                            />
                                        </div>

                                        {/* Learning Outcomes */}
                                        <div className="sm:col-span-3">
                                            <FieldHeader label="Learning Outcomes" />
                                            <div className="mt-1">
                                                <FieldArray name="outcomes">
                                                    {({push, remove}: FieldArrayRenderProps) => (
                                                        <div>
                                                            <ul>
                                                                {formik.values.outcomes?.map((outcome, outcomeIndex) => (
                                                                    <li key={outcomeIndex} className="my-2">
                                                                        <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                                                            <div className="w-full">
                                                                                <label
                                                                                    htmlFor={`outcomes.${outcomeIndex}`}
                                                                                    className="sr-only"
                                                                                >Outcome</label>
                                                                                <Field
                                                                                    type="text"
                                                                                    icon={ClipboardCheckIcon}
                                                                                    name={`outcomes.${outcomeIndex}`}
                                                                                    id={`outcomes.${outcomeIndex}`}
                                                                                    autoComplete="off"
                                                                                    maxLength="255"
                                                                                    placeholder="Outcome"
                                                                                />
                                                                            </div>
                                                                            <div className="flex-grow-0">
                                                                                <Button
                                                                                    size={Button.sizes.TINY}
                                                                                    color={Button.colors.WHITE}
                                                                                    icon={TrashIcon}
                                                                                    onClick={() => remove(outcomeIndex)}
                                                                                    disabled={(formik.values.outcomes?.length || 0) < 2}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>

                                                            <div className="mt-2">
                                                                <Button
                                                                    color={Button.colors.SECONDARY}
                                                                    icon={PlusIcon}
                                                                    size={Button.sizes.TINY}
                                                                    onClick={() => push('')}
                                                                >
                                                                    Add Outcome
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </FieldArray>
                                            </div>
                                        </div>

                                        {/* Transferable SKills */}
                                        <div className="sm:col-span-3">
                                            <FieldHeader label="Transferable Skills" />
                                            <div className="mt-1">
                                                <FieldArray name="skills">
                                                    {({push, remove}: FieldArrayRenderProps) => (
                                                        <div>
                                                            <ul>
                                                                {formik.values.skills?.map((skill, skillIndex) => (
                                                                    <li key={skillIndex} className="my-2">
                                                                        <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                                                            <div className="w-full">
                                                                                <label
                                                                                    htmlFor={`skills.${skillIndex}`}
                                                                                    className="sr-only"
                                                                                >Skill</label>
                                                                                <Field
                                                                                    type="text"
                                                                                    icon={ClipboardListIcon}
                                                                                    name={`skills.${skillIndex}`}
                                                                                    id={`skills.${skillIndex}`}
                                                                                    autoComplete="off"
                                                                                    maxLength="255"
                                                                                    placeholder="Skill"
                                                                                />
                                                                            </div>
                                                                            <div className="flex-grow-0">
                                                                                <Button
                                                                                    size={Button.sizes.TINY}
                                                                                    color={Button.colors.WHITE}
                                                                                    icon={TrashIcon}
                                                                                    onClick={() => remove(skillIndex)}
                                                                                    disabled={(formik.values.skills?.length || 0) < 2}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>

                                                            <div className="mt-2">
                                                                <Button
                                                                    color={Button.colors.SECONDARY}
                                                                    icon={PlusIcon}
                                                                    size={Button.sizes.TINY}
                                                                    onClick={() => push('')}
                                                                >
                                                                    Add Skill
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </FieldArray>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-8">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Activity Plans
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Manage the activities that make up this lesson
                                        </p>
                                    </div>
                                    <FieldArray name="activities">
                                        {({push, remove}: FieldArrayRenderProps) => (
                                            <div>
                                                <ul>
                                                    {formik.values.activities?.map((activity, index) => (
                                                        <li key={index}>
                                                            <LessonActivityPlanForm
                                                                media={props.lessonPlan?.activities[index]?.media || []}
                                                                exhibits={exhibits?.data || []}
                                                                activityCount={formik.values.activities?.length || 0}
                                                                activity={activity}
                                                                index={index}
                                                                setFieldValue={formik.setFieldValue}
                                                                removeActivity={() => remove(index)}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>

                                                <div className="mt-4">
                                                    <Button
                                                        color={Button.colors.SECONDARY}
                                                        icon={PlusIcon}
                                                        size={Button.sizes.X_SMALL}
                                                        onClick={() => push({...emptyActivity})}
                                                    >
                                                        Add Activity
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </FieldArray>
                                </div>

                                <div className="pt-8">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Status
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Make this lesson plan available for use, or keep it hidden
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-6">
                                            <Toggle label="Published"
                                                    checked={formik.values.is_published}
                                                    onChange={checked => formik.setFieldValue('is_published', checked)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <FormControls formik={formik} />
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default LessonPlanForm;